import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { policiesKeys } from "../queryKeys/queryKeys";
import {
  FiltersValuesRequest,
  OwnersListRequest,
  policiesInventoryApi,
} from "@repo/api/clients/policiesInventory.api";
import {
  OwnersFiltersValueResponse,
  OwnersListResponse,
  PreviouslyAssignedOwnersResponse,
} from "@repo/api/schemas/policiesInventory.schema";

export const useOwnersList = (payload: OwnersListRequest) => {
  return useInfiniteQuery<OwnersListResponse>({
    initialPageParam: null,
    queryKey: [...policiesKeys.ownersList(), JSON.stringify(payload)],
    queryFn: ({ pageParam }) =>
      policiesInventoryApi.getOwnersList({
        payload,
        pageParam: pageParam as string,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    retry: false,
  });
};

export const usePreviouslyAssignedOwners = (
  assetId: string,
  isBulkAssign: boolean,
) => {
  return useQuery<PreviouslyAssignedOwnersResponse>({
    queryKey: policiesKeys.previouslyAssignedOwners(assetId),
    queryFn: () => policiesInventoryApi.getPreviouslyAssignedOwners(assetId),
    retry: false,
    enabled: !!assetId && !isBulkAssign,
  });
};

export const useAssignOwnerToSigleAsset = (
  assetId: string,
  clearSelection?: () => void,
) => {
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: policiesInventoryApi.assignOwnerToSingleAsset,
    onError: () => {
      clearSelection?.();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policiesKeys.inventorySummaryData(assetId),
      });
      queryClient.invalidateQueries({
        queryKey: policiesKeys.previouslyAssignedOwners(assetId),
      });
      queryClient.invalidateQueries({
        queryKey: policiesKeys.inventoryTimelineData(assetId),
      });
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "inventorySummaryData"],
      });
      clearSelection?.();
    },
  });
  return { mutate };
};

export const useAssignOwnerToBulkAssets = (clearSelection?: () => void) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: policiesInventoryApi.assignOwnerToBulkAssets,
    onError: () => {
      clearSelection?.();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "inventorySummaryData"],
      });
      clearSelection?.();
    },
  });
  return { mutate };
};

export const useAssignOwnerToAllAssets = (clearSelection?: () => void) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: policiesInventoryApi.assignOwnerToAllAssets,
    onError: () => {
      clearSelection?.();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "inventorySummaryData"],
      });
      clearSelection?.();
    },
  });
  return { mutate };
};

export const useOwnerFiltersValues = (
  payload: FiltersValuesRequest,
  hasMenuOpened: boolean,
) => {
  return useInfiniteQuery<OwnersFiltersValueResponse>({
    initialPageParam: null,
    queryKey: [...policiesKeys.ownerFiltersValues(), JSON.stringify(payload)],
    queryFn: ({ pageParam }) =>
      policiesInventoryApi.getAssignOwnerFiltersValues({
        payload,
        pageParam: pageParam as string,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: hasMenuOpened,
    retry: false,
  });
};

export const useAssignOwnerFiltersList = (enabled = true) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: policiesKeys.assignOwnerFiltersList(),
    queryFn: () => policiesInventoryApi.getAssignOwnerFiltersList(),
    enabled,
    retry: false,
  });

  return { filtersData: data?.data ?? [], isLoading, isError };
};
